// Temple of Kulture project ecosystem — single source of truth.
// Editable from the admin CMS (added to cmsSchemas) so the team
// can shuffle projects between status columns as they evolve.

import { STATUS } from "@/lib/status";
import { IMG, TOK_ASSETS } from "@/lib/data";

export const PROJECTS = [
  // -------------------- CURRENT --------------------
  {
    id: "windrush",
    title: "Windrush Living Memory",
    desc: "Our headline immersive — projection, oral history and live performance carrying Caribbean diaspora memory forward.",
    image: TOK_ASSETS.windrushBanner,
    focal: "50% 35%",
    status: STATUS.LIVE_NOW,
    href: "/shows/windrush-immersive-experience",
  },
  {
    id: "buro-buro-mermaid",
    title: "Buro Buro Mermaid",
    desc: "A folklore-led XR piece weaving island mythology, dance and projection into a single performance.",
    image: TOK_ASSETS.sqBoraBora,
    focal: "50% 35%",
    status: STATUS.LIVE_NOW,
    href: "https://www.templeofkulture.co.uk/buro-buro-mermaid",
    external: true,
  },
  {
    id: "william-cuffay",
    title: "William Cuffay",
    desc: "Revolutionary Chartist (1788–1870) — a Black British story finally told as immersive theatre.",
    image: TOK_ASSETS.williamCuffay,
    focal: "68% 30%",
    status: STATUS.LIVE_NOW,
    href: "/shows/william-cuffay",
  },
  {
    id: "rebirth-of-a-mother",
    title: "Rebirth of a Mother",
    desc: "Silhouette story of grief, lineage and the women who carry whole worlds inside them.",
    image: TOK_ASSETS.rebirthMother,
    focal: "30% 38%",
    status: STATUS.LIVE_NOW,
    href: "/shows/rebirth-of-a-mother",
  },
  {
    id: "outcasts",
    title: "The Outcasts",
    desc: "XR theatre piece set in a neon-soaked city — diaspora identity in a world that refuses to see it.",
    image: TOK_ASSETS.outcastShow,
    focal: "70% 45%",
    status: STATUS.LIVE_NOW,
    href: "https://www.templeofkulture.co.uk/the-outcasts",
    external: true,
  },

  // -------------------- IN DEVELOPMENT --------------------
  {
    id: "yekri",
    title: "Yekri",
    desc: "A spiritual immersive piece exploring diaspora ancestry and water memory. In development with partner artists.",
    image: IMG.heroPortal,
    focal: "center",
    status: STATUS.IN_DEVELOPMENT,
  },
  {
    id: "dogma",
    title: "Dogma",
    desc: "An XR walking experience interrogating belief, dogma and freedom across cultures.",
    image: IMG.xrTheatre,
    focal: "center",
    status: STATUS.IN_DEVELOPMENT,
  },
  {
    id: "native",
    title: "Native",
    desc: "Indigenous storytelling brought into immersive form — voices, soundscape and projected memory.",
    image: IMG.immersiveNights,
    focal: "center",
    status: STATUS.IN_DEVELOPMENT,
  },
  {
    id: "roots",
    title: "Roots",
    desc: "A multi-room walking theatre tracing African diaspora roots through sound, smell and projection.",
    image: IMG.redDancer,
    focal: "center",
    status: STATUS.IN_DEVELOPMENT,
  },
  {
    id: "waterline",
    title: "Waterline",
    desc: "A youth-led immersive piece exploring climate, migration and the bodies of water that carry us.",
    image: IMG.wellness,
    focal: "center",
    status: STATUS.IN_DEVELOPMENT,
  },
  {
    id: "main-character",
    title: "Main Character",
    desc: "An immersive theatre work centring the everyday hero of diaspora life.",
    image: IMG.dance1,
    focal: "center",
    status: STATUS.IN_DEVELOPMENT,
  },
  {
    id: "mada",
    title: "Mada",
    desc: "A piece exploring matriarchal lineage in immersive form. Currently in research.",
    image: IMG.dance2,
    focal: "center",
    status: STATUS.IN_DEVELOPMENT,
  },
  {
    id: "in-my-mind",
    title: "In My Mind",
    desc: "A neurodiverse-led XR experience mapping the textures of thought.",
    image: IMG.levelUp,
    focal: "center",
    status: STATUS.IN_DEVELOPMENT,
  },
  {
    id: "we-the-kulture",
    title: "We The Kulture",
    desc: "A community-built immersive anthem celebrating global majority creativity.",
    image: IMG.playground,
    focal: "center",
    status: STATUS.IN_DEVELOPMENT,
  },

  // -------------------- PLANNED --------------------
  {
    id: "enter-the-temple",
    title: "Enter The Temple",
    desc: "Our flagship signature ritual experience — entry into the Temple itself, planned for 2026.",
    image: IMG.hero,
    focal: "center",
    status: STATUS.COMING_SOON,
  },
  {
    id: "tt-time",
    title: "TT Time",
    desc: "A planned community ritual series — heritage, healing and time-bending storytelling.",
    image: IMG.windrushEvergreen,
    focal: "center",
    status: STATUS.COMING_SOON,
  },
  {
    id: "echoes-of-wisdom",
    title: "Echoes of Wisdom",
    desc: "Oral-history meets immersive performance — elder voices carried into XR.",
    image: TOK_ASSETS.windrushBanner,
    focal: "50% 50%",
    status: STATUS.COMING_SOON,
  },
  {
    id: "kultural-storytellers",
    title: "Kultural Storytellers",
    desc: "A planned national programme commissioning emerging storytellers from global majority communities.",
    image: IMG.heroPortal,
    focal: "center",
    status: STATUS.COMING_SOON,
  },

  // -------------------- FUTURE VISION --------------------
  {
    id: "tok-studio-vr-cafe",
    title: "Temple of Kulture Studio & VR Café",
    desc: "Our planned permanent home — a hybrid studio, café and walk-in VR experience hub.",
    image: IMG.corporate,
    focal: "center",
    status: STATUS.FUTURE_VISION,
  },
  {
    id: "vr-gaming-pop-up",
    title: "VR Gaming Pop Up",
    desc: "Touring VR gaming activations in partnership with festivals and brands.",
    image: IMG.playground,
    focal: "center",
    status: STATUS.FUTURE_VISION,
  },
  {
    id: "future-productions",
    title: "Future Immersive Productions",
    desc: "Ideas in incubation — partner with us to bring the next chapter to life.",
    image: IMG.xrTheatre,
    focal: "center",
    status: STATUS.FUTURE_VISION,
  },
];

// Helper — group projects by status column for the /projects page.
export function groupedProjects() {
  const groups = {
    [STATUS.LIVE_NOW]: { label: "Current", description: "Live now — book or experience these immersive works today.", items: [] },
    [STATUS.IN_DEVELOPMENT]: { label: "In Development", description: "Actively in production. Notify me when launched.", items: [] },
    [STATUS.COMING_SOON]: { label: "Planned", description: "Locked in for the road ahead — join the waitlist now.", items: [] },
    [STATUS.FUTURE_VISION]: { label: "Future Vision", description: "Ideas seeking funders, venues and creative partners.", items: [] },
  };
  PROJECTS.forEach((p) => {
    if (groups[p.status]) groups[p.status].items.push(p);
  });
  return groups;
}
